import type { BillingPlanLimitFieldKey, BillingPlanLimits } from '@/constants/billing.constant'
import { BILLING_PLAN_LIMIT_FIELDS } from '@/constants/billing.constant'

import { PLAN_LIMIT_REACHED_CODE, planLimitReachedMessage } from './plan-usage-keys.util'

export type PlanLimitErrorPayload = {
    code: typeof PLAN_LIMIT_REACHED_CODE
    key: BillingPlanLimitFieldKey
}

export type PlanUsageSnapshot = Partial<Record<BillingPlanLimitFieldKey, number>>

const LIMIT_KEY_SET = new Set<string>(BILLING_PLAN_LIMIT_FIELDS.map((f) => f.name))

export function isBillingPlanLimitFieldKey(value: unknown): value is BillingPlanLimitFieldKey {
    return typeof value === 'string' && LIMIT_KEY_SET.has(value)
}

export function planLimitReachedError(key: BillingPlanLimitFieldKey): PlanLimitErrorPayload {
    return { code: PLAN_LIMIT_REACHED_CODE, key }
}

export function planLimitReachedApiMessage(key: BillingPlanLimitFieldKey): string {
    return planLimitReachedMessage(key)
}

export function planLimitReachedBody(key: BillingPlanLimitFieldKey) {
    return {
        statusCode: 403,
        data: null,
        message: planLimitReachedApiMessage(key),
        success: false,
        errors: [planLimitReachedError(key)],
    }
}

/** Whether usage + delta would exceed a finite limit (shared client/server math). */
export function wouldExceedPlanLimit(
    limits: BillingPlanLimits,
    usage: PlanUsageSnapshot,
    key: BillingPlanLimitFieldKey,
    delta = 1
): boolean {
    const limit = limits[key]
    if (limit == null) return false
    const current = usage[key] ?? 0
    return current + delta > limit
}

/**
 * Detect plan-limit failures from axios / ApiResponse shapes.
 * Prefers structured `errors[].code === PLAN_LIMIT_REACHED`, falls back to message parse.
 */
export function isPlanLimitError(err: unknown): { key: BillingPlanLimitFieldKey } | null {
    const responseData = extractResponseData(err)
    if (responseData) {
        const fromErrors = findKeyInErrors(responseData.errors)
        if (fromErrors) return { key: fromErrors }

        const fromMessage = parseKeyFromMessage(responseData.message)
        if (fromMessage) return { key: fromMessage }
    }

    if (err instanceof Error) {
        const fromMessage = parseKeyFromMessage(err.message)
        if (fromMessage) return { key: fromMessage }
    }

    return null
}

function extractResponseData(err: unknown): { message?: unknown; errors?: unknown } | null {
    if (err == null || typeof err !== 'object') return null
    const maybeAxios = err as { response?: { data?: { message?: unknown; errors?: unknown } } }
    if (maybeAxios.response?.data && typeof maybeAxios.response.data === 'object') {
        return maybeAxios.response.data
    }
    const direct = err as { message?: unknown; errors?: unknown }
    if (direct.message != null || direct.errors != null) {
        return direct
    }
    return null
}

function findKeyInErrors(errors: unknown): BillingPlanLimitFieldKey | null {
    if (!Array.isArray(errors)) return null
    for (const entry of errors) {
        if (entry == null || typeof entry !== 'object') continue
        const e = entry as { code?: unknown; key?: unknown }
        if (e.code === PLAN_LIMIT_REACHED_CODE && isBillingPlanLimitFieldKey(e.key)) {
            return e.key
        }
    }
    return null
}

function parseKeyFromMessage(message: unknown): BillingPlanLimitFieldKey | null {
    if (typeof message !== 'string') return null
    const match = /^Plan limit reached for ([a-zA-Z0-9_]+)$/.exec(message.trim())
    if (!match?.[1] || !isBillingPlanLimitFieldKey(match[1])) return null
    return match[1]
}
